import type { DesignSystemRef, TokenMap } from './kernel-contract.js';

/**
 * Stamp the kernel-resolved design system at the document root BEFORE first paint.
 *
 * The kernel emits {@link DesignSystemRef.tokens} as DATA (custom-property name → value);
 * the host turns that data into styles by writing each pair onto the root element via the
 * CSSOM. This is deliberately NOT an injected `<style>` block or a `style="…"` attribute in
 * markup, so a strict `style-src 'self'` CSP is satisfied without `'unsafe-inline'` — the
 * same discipline `@caos/ui` uses for its constructable stylesheets. The values are inherited
 * custom properties that pierce every component's shadow boundary, so the whole surface
 * re-skins with zero per-component work.
 *
 * The full `--caos-*` substrate is the compiled `design-system` floor the host bakes in; the
 * token map here is the IDENTITY/override seam (the brand tokens for the baked default, a
 * Theme's own tokens for an installed system). Applying it over the floor is the same single
 * overlay resolution the engine uses post-install — here client-side, pre-paint.
 *
 * The host code path is identical whether the tokens came from the frozen default or a
 * deployed override (ADR §4): both arrive as a {@link DesignSystemRef}.
 */

/**
 * CAOS-294 — the light/dark MODE-VARIANT tokens the host must NOT stamp inline. These are the
 * surfaces / neutrals / heat / override colours `@caos/ui`'s tokens.css restates per
 * `:root[data-theme=…]` (and `@media prefers-color-scheme`). An installed theme that stamped them
 * inline would FREEZE the org in one mode — inline styles outrank the stylesheet cascade — so the
 * theme switcher (theme.ts) could never take effect. Withholding them here hands light/dark entirely
 * to the cascade, while brand IDENTITY tokens (accent, fonts, radius, wordmark, chrome, semantics)
 * still stamp inline as the override seam. See theme.ts for the mode cascade this protects.
 */
const MODE_VARIANT_TOKENS: ReadonlySet<string> = new Set([
  '--caos-color-bg',
  '--caos-color-surface',
  '--caos-color-surface-2',
  '--caos-color-text',
  '--caos-color-text-muted',
  '--caos-color-text-subtle',
  '--caos-color-text-ghost',
  '--caos-color-border',
  '--caos-color-border-strong',
  '--caos-neutral-text',
  '--caos-neutral-text-muted',
  '--caos-neutral-text-subtle',
  '--caos-neutral-text-ghost',
  '--caos-neutral-border',
  '--caos-neutral-border-strong',
  '--caos-color-heat',
  '--caos-color-accent',
  '--caos-scrollbar-thumb',
  '--caos-color-override-bg',
  '--caos-color-override-border',
  '--caos-color-override-text',
]);

/** The custom-property names the host stamped on the last apply, so a swap can clear stale ones. */
let stampedKeys: readonly string[] = [];

/** Apply a full design system: its token seam at root, plus stable data markers for the theme. */
export function applyDesignSystem(
  ref: DesignSystemRef,
  root: HTMLElement = document.documentElement,
): void {
  applyTokens(ref.tokens, root);
  root.setAttribute('data-design-system', ref.key);
  root.setAttribute('data-design-source', ref.source);
  applyFavicon(ref.tokens);
  // CAOS-522 — `<caos-topbar>` re-reads its brand lockup (wordmark/logo) ONLY on a `data-brand`
  // mutation (theme.ts's light/dark palette-family marker — see topbar.ts's brandObserver). That
  // marker never otherwise changes after boot, so an org's live edit (Theme & Branding save, or
  // its client-side preview) would stamp new tokens with no visible repaint until a full reload.
  // Re-setting it to its own current value still queues a MutationRecord (setAttribute always
  // does, regardless of whether the value changed), so this is the real trigger for the "no
  // reload" white-label swap the topbar was already built to support.
  const brand = root.getAttribute('data-brand');
  if (brand !== null) root.setAttribute('data-brand', brand);
}

/**
 * The href the platform's OWN baked favicon `<link>` carried, captured once on the first apply —
 * the fallback once an org's `--brand-favicon-url` token is cleared. Deliberately NOT the `<link>`
 * element itself: caching the element would go stale the moment anything ever replaced it, quietly
 * breaking every apply after that (a real bug this module had — caught by its own test, CAOS-522).
 * The element itself is re-queried on every call instead; that is cheap and never stale.
 */
let bakedFaviconHref: string | null = null;

/**
 * CAOS-522 — apply the org's favicon: the `--brand-favicon-url` token when the resolved design
 * system carries one, else the platform's own baked favicon (the tab icon this host shipped
 * with, captured on first read) — so clearing an org's customization restores the default rather
 * than leaving a stale tab icon behind.
 */
export function applyFavicon(tokens: TokenMap): void {
  let link: HTMLLinkElement | null;
  try {
    link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  } catch {
    return; // no DOM (tests) — favicon application is a no-op, never a hard failure.
  }
  if (!link) return;
  if (bakedFaviconHref === null) bakedFaviconHref = link.href;
  const raw = tokens['--brand-favicon-url']?.trim();
  const url = raw ? raw.replace(/^["']|["']$/g, '').trim() : '';
  link.href = url || bakedFaviconHref || link.href;
}

/**
 * Write a token map onto the root, removing any property the host stamped previously that is
 * absent from the new map — so a token SWAP (AC-2) leaves no stale override behind and every
 * `@caos/ui` component repaints from the new seam.
 */
export function applyTokens(tokens: TokenMap, root: HTMLElement = document.documentElement): void {
  // Withhold the light/dark mode-variant tokens (CAOS-294): those belong to the tokens.css cascade so
  // the theme switch can flip them; stamping them inline here would freeze the org in one mode.
  const nextKeys = Object.keys(tokens).filter((k) => !MODE_VARIANT_TOKENS.has(k));
  const nextSet = new Set(nextKeys);
  for (const stale of stampedKeys) {
    if (!nextSet.has(stale)) root.style.removeProperty(stale);
  }
  for (const name of nextKeys) {
    root.style.setProperty(name, tokens[name]!);
  }
  stampedKeys = nextKeys;
}
